/**
 * Queue column model (Tasks 2/3/6). A single descriptor list drives the table
 * header + cells, the sortable-header affordance (Task 2), the column show/hide +
 * reorder settings (Task 3), the CSV export (`text`, Task 3), and the age/urgency
 * treatment (Task 6). Keeping the columns data-driven means the table renders
 * whatever ordered/visible subset the container hands it — no per-column
 * branching in <OrderTable/>.
 *
 * `sortKey` (when present) marks a column the worker can sort server-side; the
 * customs column is intentionally NOT sortable because its value is computed at
 * read-time (computeOrderCustoms), not a stored field, so it can't be ordered
 * across paginated results. Cells stay pure (data in, nodes out); `text` gives
 * the same column a plain-string value for CSV export.
 */
import type { ReactNode } from "react";
import type { OrderRow } from "../../lib/api.js";
import { formatAge, ageUrgency } from "../../lib/age.js";
import { weightKg, humanizeCode, humanizeServiceCode, storeBadge, storeName, formatMoney } from "../../lib/format.js";
import { countryName } from "../../lib/countries.js";
import { isPriorityClass, statusLabel } from "../../lib/status.js";
import { Badge, StatusChip, CarrierMark } from "../../components/ui/index.js";
import { Icon } from "../../components/ui/Icon.js";

export interface QueueColumnCtx {
  now: number;
}

export interface QueueColumn {
  /** Stable id — used for persistence (Task 3) and as the React key. */
  key: string;
  header: string;
  /** Right-aligned numeric column (tabular numerals). */
  num?: boolean;
  /** When set, the column is sortable; the value is the worker `sort` param. */
  sortKey?: string;
  /** Always visible / first — excluded from hide + reorder (Task 3). */
  required?: boolean;
  cell: (o: OrderRow, ctx: QueueColumnCtx) => ReactNode;
  /** Plain-string value of this column for CSV export (Task 3). */
  text: (o: OrderRow, ctx: QueueColumnCtx) => string;
}

/** Grams → kg string for export ("" when unknown — cleaner than a dash in CSV). */
function weightText(o: OrderRow): string {
  const g = o.theoreticalWeightG ?? o.itemsWeightG ?? null;
  return g == null ? "" : (g / 1000).toFixed(2);
}

export const QUEUE_COLUMNS: QueueColumn[] = [
  {
    key: "order",
    header: "Order",
    sortKey: "order",
    required: true,
    cell: (o) => {
      const priority = isPriorityClass(o.serviceClass);
      return (
        <span className="q-order-cell">
          <span className="q-order">
            {priority && <Icon name="bolt" className="q-order__bolt" title="Express" />}
            {o.name}
          </span>
          {o.customerName && (
            <span className="q-customer truncate" title={o.customerName}>
              {o.customerName}
            </span>
          )}
        </span>
      );
    },
    text: (o) => (o.customerName ? `${o.name} — ${o.customerName}` : o.name),
  },
  {
    key: "store",
    header: "Store",
    sortKey: "store",
    cell: (o) => <Badge title={storeName(o)}>{storeBadge(o)}</Badge>,
    text: (o) => storeName(o),
  },
  {
    key: "destination",
    header: "Destination",
    cell: (o) => (
      <span className="q-dest">
        {o.hasPickupPoint && <Icon name="pin" title="Pickup point" />}
        <span className="truncate" title={o.destinationCountry}>
          {countryName(o.destinationCountry) || "—"}
        </span>
      </span>
    ),
    text: (o) => countryName(o.destinationCountry) || o.destinationCountry || "",
  },
  {
    key: "items",
    header: "Items",
    num: true,
    sortKey: "items",
    cell: (o) => <span className="tnum">{o.lineCount}</span>,
    text: (o) => String(o.lineCount),
  },
  {
    key: "weight",
    header: "Weight",
    num: true,
    sortKey: "weight",
    cell: (o) => <span className="tnum">{weightKg(o.theoreticalWeightG, o.itemsWeightG)}</span>,
    text: weightText,
  },
  {
    key: "method",
    header: "Method",
    cell: (o) =>
      o.shippingMethodTitle ? (
        <span className="q-method truncate" title={o.shippingMethodTitle}>
          {o.shippingMethodTitle}
        </span>
      ) : (
        <span className="muted">—</span>
      ),
    text: (o) => o.shippingMethodTitle ?? "",
  },
  {
    key: "carrier",
    header: "Carrier",
    cell: (o) =>
      o.carrierCode ? (
        <span className="q-carrier">
          <CarrierMark code={o.carrierCode} showName />
          {o.serviceCode && (
            <span className="q-carrier__svc truncate" title={o.serviceCode}>
              {humanizeServiceCode(o.serviceCode)}
            </span>
          )}
        </span>
      ) : (
        <span className="muted">unrouted</span>
      ),
    text: (o) =>
      o.carrierCode
        ? `${humanizeCode(o.carrierCode)}${o.serviceCode ? ` ${humanizeServiceCode(o.serviceCode)}` : ""}`
        : "",
  },
  {
    key: "customs",
    header: "Customs",
    cell: (o) =>
      o.customs ? (
        <span className="q-customs">
          {o.customs.incoterm ? (
            <Badge title="Incoterm">{o.customs.incoterm}</Badge>
          ) : (
            <span className="muted">no lane</span>
          )}
          {o.customs.customsValueTotal != null && (
            <span className="q-customs__val tnum" title="Customs value">
              {formatMoney(o.customs.customsValueTotal, o.customs.customsCurrency)}
            </span>
          )}
        </span>
      ) : (
        <span className="muted">—</span>
      ),
    text: (o) =>
      o.customs
        ? [o.customs.incoterm ?? "", o.customs.customsValueTotal != null ? formatMoney(o.customs.customsValueTotal, o.customs.customsCurrency) : ""]
            .filter(Boolean)
            .join(" ")
        : "",
  },
  {
    key: "status",
    header: "Status",
    sortKey: "status",
    cell: (o) => (
      <>
        <StatusChip status={o.status} />
        {o.exceptionReason && <div className="q-exc truncate">{o.exceptionReason}</div>}
      </>
    ),
    text: (o) => statusLabel(o.status),
  },
  {
    key: "age",
    header: "Age",
    num: true,
    sortKey: "age",
    cell: (o, { now }) => {
      const urgency = ageUrgency(o.createdAtShopify, o.serviceClass, now);
      return (
        <span className="q-age-cell" title={o.createdAtShopify}>
          <span className={"q-age tnum" + (urgency ? ` q-age--${urgency.tone}` : "")}>
            {formatAge(o.createdAtShopify, now)}
          </span>
          {urgency && <span className={`q-urgency q-urgency--${urgency.tone}`}>{urgency.label}</span>}
        </span>
      );
    },
    text: (o, { now }) => formatAge(o.createdAtShopify, now),
  },
];

/** All column keys in their canonical (default) order. */
export const QUEUE_COLUMN_KEYS = QUEUE_COLUMNS.map((c) => c.key);
